package com.sddamico.draftmachine.ds

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover

/**
 * Draft Machine — global base document defaults. Kotlin translation of the document-level
 * rules in `tokens/base.css`. The page background/foreground come from the Silk color palette
 * (see SiteTheme), so this only owns the type defaults; the `@keyframes`, reduced-motion reset,
 * and `::selection` rules live in the injected <style> block in build.gradle.kts.
 */
@InitSilk
fun initDmBase(ctx: InitSilkContext) {
  ctx.stylesheet.registerStyleBase("body") {
    Modifier
      .fontFamily(*DmType.body)
      .fontSize(DmType.textBase)
      .lineHeight(DmType.leadingNormal)
      .styleModifier {
        property("-webkit-font-smoothing", "antialiased")
        property("text-rendering", "optimizeLegibility")
      }
  }
  // Headings inherit the slab voice but stay unopinionated on size.
  ctx.stylesheet.registerStyleBase("h1, h2, h3, h4") {
    Modifier
      .fontFamily(*DmType.heading)
      .fontWeight(DmType.weightBold)
      .lineHeight(DmType.leadingSnug)
      .color(DmColor.text)
  }
}

// ---- Display helpers (raw CSS — the typed DisplayStyle enum spellings vary across the
// Compose-HTML / Kobweb packages, so we pin the exact CSS keyword here). ----

fun Modifier.displayBlock() = styleModifier { property("display", "block") }
fun Modifier.displayFlex() = styleModifier { property("display", "flex") }
fun Modifier.displayInlineFlex() = styleModifier { property("display", "inline-flex") }
fun Modifier.displayInlineBlock() = styleModifier { property("display", "inline-block") }
fun Modifier.displayGrid() = styleModifier { property("display", "grid") }

/**
 * Shared interaction feedback for pressable controls (Button, IconButton, Tabs, Switch…).
 * Hover brightens (`filter`), press translates down + scales in (`transform`), and keyboard
 * focus shows a copper outline. It only touches `filter` / `transform` / `outline`, so a
 * control's own inline background / border / box-shadow values are left intact.
 *
 * Apply as `PressableStyle.toModifier().then(<surface modifier>)`.
 */
val PressableStyle = CssStyle {
  base {
    Modifier.transitionRaw(
      "transform ${DmMotion.durFast} ${DmMotion.easeOut}, " +
        "filter ${DmMotion.durFast} ${DmMotion.easeOut}"
    )
  }
  hover { Modifier.styleModifier { property("filter", "brightness(1.08)") } }
  active { Modifier.styleModifier { property("transform", "translateY(1px) scale(0.99)") } }
  focusVisible {
    Modifier.styleModifier {
      property("outline", "2px solid #D6935C")
      property("outline-offset", "2px")
    }
  }
}
